import { useState } from 'react'
import { useInstalacao } from '../dados/instalacao.js'

/// Convite para instalar o QSMS na tela inicial do celular.
///
/// Instalado, o app abre pelo ícone, em tela cheia, sem a barra de endereço — e é
/// o jeito certo de usar em campo: ninguém deveria precisar lembrar um endereço
/// nem achar a aba certa no navegador, de luva, no sol.
///
/// Some sozinho quando o app já está instalado, e por 30 dias quando a pessoa
/// toca em "Agora não".

const CHAVE_DISPENSA = 'qsms.instalacao.dispensadaEm'
const DIAS_DISPENSA = 30

function dispensadoRecentemente(): boolean {
  try {
    const em = Number(window.localStorage.getItem(CHAVE_DISPENSA) ?? 0)
    return Date.now() - em < DIAS_DISPENSA * 86_400_000
  } catch {
    return false
  }
}

export function InstalarApp() {
  const { podeInstalar, instalado, plataforma, instalar } = useInstalacao()
  const [dispensado, setDispensado] = useState(dispensadoRecentemente)
  const [instalando, setInstalando] = useState(false)

  if (instalado || dispensado) return null
  // No computador, só quando o navegador oferece instalar (Chrome e Edge oferecem).
  if (!podeInstalar && plataforma === 'outro') return null

  function dispensar() {
    try {
      window.localStorage.setItem(CHAVE_DISPENSA, String(Date.now()))
    } catch {
      // Sem armazenamento, o convite volta na próxima abertura. Não é grave.
    }
    setDispensado(true)
  }

  return (
    <div className="cartao mb-4 flex gap-3 border-marinho/20 p-3">
      <img src="/apple-touch-icon.png" alt="" className="h-11 w-11 shrink-0 rounded-xl" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900">Instale o QSMS no celular</p>
        <p className="text-xs text-slate-600">
          Abre direto pelo ícone na tela inicial, em tela cheia, e funciona sem internet.
        </p>

        {podeInstalar ? (
          <button
            onClick={async () => {
              setInstalando(true)
              try {
                await instalar()
              } finally {
                setInstalando(false)
              }
            }}
            disabled={instalando}
            className="botao-primario mt-2 !min-h-0 !py-1.5 text-sm"
          >
            {instalando ? 'Instalando…' : 'Instalar aplicativo'}
          </button>
        ) : plataforma === 'ios' ? (
          <ol className="mt-2 list-decimal space-y-0.5 pl-4 text-xs text-slate-700">
            <li>
              No <strong>Safari</strong>, toque em Compartilhar{' '}
              <IconeCompartilhar /> na barra de baixo.
            </li>
            <li>
              Escolha <strong>Adicionar à Tela de Início</strong> e toque em <strong>Adicionar</strong>.
            </li>
            <li>Abra pelo ícone novo e entre com a conta Ápia.</li>
          </ol>
        ) : (
          <p className="mt-2 text-xs text-slate-700">
            No menu <strong>⋮</strong> do Chrome, toque em <strong>Instalar aplicativo</strong> ou{' '}
            <strong>Adicionar à tela inicial</strong>.
          </p>
        )}

        {plataforma === 'ios' && (
          <p className="mt-2 text-[11px] text-slate-500">
            No iPhone, o app instalado guarda os dados separado do Safari: envie o que estiver pendente
            aqui antes de passar a usar o ícone.
          </p>
        )}

        <button onClick={dispensar} className="mt-1 text-xs font-medium text-slate-500 hover:underline">
          Agora não
        </button>
      </div>
    </div>
  )
}

function IconeCompartilhar() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="inline h-4 w-4 align-text-bottom text-marinho"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-label="ícone de compartilhar"
    >
      <path d="M12 3v12M8 7l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 11H5v10h14V11h-1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
